import React, { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
    cancelEventRequest,
    createEventRequest,
    downloadEventAttendanceSheetRequest,
    fetchEventsRequest,
    replanifyEventRequest,
    sendEventCalendarRequest,
    updateEventRequest,
} from "../../../redux/actions/eventsActions";
import { declareMyEventAbsenceRequest } from "../../../redux/actions/eventAttendanceActions";
import { fetchModulesCleRequest } from "../../../redux/actions/modulesCleActions";
import { fetchSallesRequest } from "../../../redux/actions/sallesActions";
import {
    fetchAssignableUsersRequest,
} from "../../../redux/actions/assignmentActions";
import {
    cancelEventDemandeRequest,
    createEventDemandeByLearnerRequest,
    createEventDemandeByManagerRequest,
    createEventDemandeByRfRequest,
    getMyDemandesRequest,
    withdrawEventDemandeRequest,
} from "../../../redux/actions/demandesActions";
import { getDirectReportsRequest } from "../../../redux/actions/userActions";
import { getCompanyDemandesRequest } from "../../../redux/actions/demandesActions";
import UsersPickerModal from "../../Formation/ResponsableFormation/RessourcesJs/demandes/UsersPickerModal";
import BulkAssignmentModal from "../../Formation/ResponsableFormation/RessourcesJs/assignments/BulkAssignmentModal";
import CalendarToolbar from "../components/CalendarToolbar";
import CalendarFilters from "../components/CalendarFilters";
import CalendarLegend from "../components/CalendarLegend";
import CalendarRoleBanner from "../components/CalendarRoleBanner";
import EventDetailsModal from "../components/EventDetailsModal";
import EventAbsenceModal from "../components/EventAbsenceModal";
import EventFormModal from "../components/EventFormModal";
import EventRescheduleModal from "../components/EventRescheduleModal";
import EventWithdrawalRequestModal from "../components/EventWithdrawalRequestModal";
import CalendarLoader from "../components/CalendarLoader";
import CalendarCollaboratorsSidebar from "../components/CalendarCollaboratorsSidebar";
import { fetchAssignmentsMapApi, fetchEventDetailsApi } from "../services/calendarApi";
import {
    buildVisibleEvents,
    getEventRegistrationAvailability,
    getParticipantUserId,
    getUserDisplayName,
    mapEventToCalendarEntry,
} from "../utils/calendarHelpers";
import { getCalendarRoleConfig } from "../utils/calendarRoleUtils";
import { getAvailableCalendarStatuses } from "../utils/calendarStatusMap";
import "../styles/calendar.css";

function CalendarPage() {
    const dispatch = useDispatch();
    const calendarRef = useRef(null);

    const eventsState = useSelector((state) => state.events || {});
    const userState = useSelector((state) => state.user || {});
    const assignmentsState = useSelector((state) => state.assignments || {});
    const authUser = useSelector((state) => state.auth?.user);
    const currentRole = useSelector((state) => state.role?.selectedRole);
    const modulesState = useSelector((state) => state.modulesCle || {});
    const sallesState = useSelector((state) => state.salles || {});
    const demandesState = useSelector((state) => state.demandes || {});

    const currentUser = authUser;
    const events = eventsState.events || [];
    const modules = modulesState.modulesCle || [];
    const salles = sallesState.salles || [];
    const directReports = userState.directReports || [];
    const assignableUsers = assignmentsState.assignableUsers || [];
    const roleConfig = useMemo(() => getCalendarRoleConfig(currentRole), [currentRole]);
    const attendanceState = useSelector((state) => state.eventAttendance || {});
    const availableStatuses = useMemo(() => getAvailableCalendarStatuses(events), [events]);
    const [currentView, setCurrentView] = useState("dayGridMonth");
    const [activeRoleView, setActiveRoleView] = useState(roleConfig.defaultView);
    const [calendarTitle, setCalendarTitle] = useState("Agenda");
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCollaboratorId, setSelectedCollaboratorId] = useState("");
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [formState, setFormState] = useState({ open: false, mode: "create", event: null });
    const [rescheduleState, setRescheduleState] = useState({ open: false, event: null });
    const [assignmentOpen, setAssignmentOpen] = useState(false);
    const [registrationPickerOpen, setRegistrationPickerOpen] = useState(false);
    const [absenceModalOpen, setAbsenceModalOpen] = useState(false);
    const [withdrawalModalOpen, setWithdrawalModalOpen] = useState(false);
    const [assignmentsMap, setAssignmentsMap] = useState({});
    const [assignmentsLoading, setAssignmentsLoading] = useState(false);

    const pageLoading = Boolean(eventsState.loading || modulesState.loading || sallesState.loading);
    const needsAssignments = roleConfig.isManager || roleConfig.isCollaborator || roleConfig.isRh;

    useEffect(() => {
        setActiveRoleView(roleConfig.defaultView);
    }, [roleConfig.defaultView]);

    useEffect(() => {
        if (activeRoleView !== "TEAM_EVENTS" && selectedCollaboratorId) {
            setSelectedCollaboratorId("");
        }
    }, [activeRoleView, selectedCollaboratorId]);

    useEffect(() => {
        dispatch(fetchEventsRequest());
        dispatch(fetchModulesCleRequest());
        dispatch(fetchSallesRequest());

        if (roleConfig.canAssign || roleConfig.canSelfRegister || roleConfig.isManager) {
            dispatch(fetchAssignableUsersRequest());
        }
        
        if (roleConfig.isRh || roleConfig.isManager) {
            dispatch(getCompanyDemandesRequest());
        }

        if (roleConfig.isCollaborator) {
            dispatch(getMyDemandesRequest());
        }
    }, [dispatch, roleConfig.canAssign, roleConfig.canSelfRegister, roleConfig.isCollaborator, roleConfig.isManager, roleConfig.isRh]);

    useEffect(() => {
        if (roleConfig.isManager && currentUser?.id) {
            dispatch(getDirectReportsRequest(currentUser.id));
        }
    }, [dispatch, roleConfig.isManager, currentUser?.id]);

    useEffect(() => {
        let active = true;

        const loadAssignments = async () => {
            if (!needsAssignments || !events.length) {
                if (active) setAssignmentsMap({});
                return;
            }

            setAssignmentsLoading(true);
            try {
                const map = await fetchAssignmentsMapApi(events.map((event) => event.id));
                if (active) setAssignmentsMap(map);
            } finally {
                if (active) setAssignmentsLoading(false);
            }
        };

        loadAssignments();

        return () => {
            active = false;
        };
    }, [events, needsAssignments]);

    useEffect(() => {
        const assignedUsersByEvent = assignmentsState.assignedUsersByEvent || {};
        if (!Object.keys(assignedUsersByEvent).length) return;
        setAssignmentsMap((current) => ({ ...current, ...assignedUsersByEvent }));
    }, [assignmentsState.assignedUsersByEvent]);

    useEffect(() => {
        if (!selectedStatuses.length && availableStatuses.length) {
            setSelectedStatuses(availableStatuses.map((status) => status.value));
        }
    }, [availableStatuses, selectedStatuses.length]);

    const managerDirectReports = useMemo(() => {
        if (!roleConfig.isManager || !currentUser?.id) return [];

        return (directReports || []).filter((report) => {
            const managerId = report?.managerId ?? report?.manager?.id ?? report?.manager?.userId ?? null;
            return managerId == null ? true : Number(managerId) === Number(currentUser.id);
        });
    }, [currentUser?.id, directReports, roleConfig.isManager]);

    const visibleEvents = useMemo(
        () =>
            buildVisibleEvents({
                events,
                selectedStatuses,
                searchTerm,
                roleConfig,
                currentUser,
                directReports: managerDirectReports,
                assignmentsMap,
                selectedCollaboratorId,
                activeView: activeRoleView,
            }),
        [activeRoleView, assignmentsMap, currentUser, events, managerDirectReports, roleConfig, searchTerm, selectedCollaboratorId, selectedStatuses]
    );

    const calendarEvents = useMemo(
        () => visibleEvents.map((event) => mapEventToCalendarEntry(event, assignmentsMap)),
        [assignmentsMap, visibleEvents]
    );

    useEffect(() => {
        if (!searchTerm || !visibleEvents.length) return;

        const api = calendarRef.current?.getApi();
        if (!api) return;

        const firstEvent = visibleEvents[0];
        const eventDate = new Date(firstEvent.dateDebut);
        if (Number.isNaN(eventDate.getTime())) return;

        if (eventDate < api.view.activeStart || eventDate >= api.view.activeEnd) {
            api.gotoDate(eventDate);
        }
    }, [searchTerm, visibleEvents]);

    const refreshSelectedEvent = async (eventId) => {
        const response = await fetchEventDetailsApi(eventId);
        const participants = assignmentsMap[eventId] || [];
        return { ...response.data, participants };
    };

    const handleCalendarRefresh = () => {
        dispatch(fetchEventsRequest());
    };

    const handleToggleStatus = (status) => {
        setSelectedStatuses((current) =>
            current.includes(status)
                ? current.filter((value) => value !== status)
                : [...current, status]
        );
    };

    const handleResetStatuses = () => {
        setSelectedStatuses(availableStatuses.map((status) => status.value));
    };

    const handleViewChange = (nextView) => {
        setCurrentView(nextView);
        const api = calendarRef.current?.getApi();
        if (api) api.changeView(nextView);
    };

    const handleCalendarNav = (action) => {
        const api = calendarRef.current?.getApi();
        if (!api) return;
        api[action]();
    };

    const openEventDetails = async (eventId) => {
        const detailedEvent = await refreshSelectedEvent(eventId);
        setSelectedEvent(detailedEvent);
        setDetailsOpen(true);
    };

    const handleEventClick = async (clickInfo) => {
        await openEventDetails(Number(clickInfo.event.id));
    };

    const handleCreateSubmit = (payload) => {
        dispatch(createEventRequest(payload));
        setFormState({ open: false, mode: "create", event: null });
    };

    const handleUpdateSubmit = (payload) => {
        dispatch(updateEventRequest(formState.event.id, payload));
        setFormState({ open: false, mode: "create", event: null });
    };

    const handleRescheduleSubmit = (payload) => {
        dispatch(replanifyEventRequest(payload));
        setRescheduleState({ open: false, event: null });
    };

    const confirmCancellation = async () => {
        if (!selectedEvent?.id) return;

        const result = await Swal.fire({
            icon: "warning",
            title: "Annuler cet événement ?",
            text: "L'événement sera conservé avec le statut ANNULÉ.",
            showCancelButton: true,
            confirmButtonText: "Oui, annuler",
            cancelButtonText: "Fermer",
            confirmButtonColor: "#ee645f",
        });

        if (result.isConfirmed) {
            dispatch(cancelEventRequest(selectedEvent.id));
            setDetailsOpen(false);
        }
    };

    const handleEventDrop = async (eventDropInfo) => {
        if (!roleConfig.canReplanify) {
            eventDropInfo.revert();
            return;
        }

        const result = await Swal.fire({
            icon: "question",
            title: "Confirmer la replanification ?",
            text: "La nouvelle plage sera envoyée au backend via l'API existante.",
            showCancelButton: true,
            confirmButtonText: "Confirmer",
            cancelButtonText: "Annuler",
        });

        if (!result.isConfirmed) {
            eventDropInfo.revert();
            return;
        }

        dispatch(
            replanifyEventRequest({
                id: Number(eventDropInfo.event.id),
                dateDebut: eventDropInfo.event.start?.toISOString() || null,
                dateFin: eventDropInfo.event.end?.toISOString() || eventDropInfo.event.start?.toISOString() || null,
            })
        );
    };

    const handleSendInvitations = () => {
        if (!selectedEvent?.id) return;
        dispatch(sendEventCalendarRequest(selectedEvent.id, "SEND_INITIAL_INVITATION"));
    };

    const handleDownloadAttendanceSheet = () => {
        if (!selectedEvent?.id) return;
        dispatch(downloadEventAttendanceSheetRequest(selectedEvent.id));
    };

    const handleAssign = () => {
        if (!selectedEvent?.id) return;
        setDetailsOpen(false);
        setAssignmentOpen(true);
    };

    const registrationCandidates = useMemo(() => {
        if (roleConfig.isManager) return managerDirectReports;

        if (roleConfig.isRh) {
            return (assignableUsers || []).filter((user) => user?.id != null);
        }

        return [];
    }, [assignableUsers, managerDirectReports, roleConfig.isManager, roleConfig.isRh]);

    const approvedRegistrationIds = useMemo(() => {
        const allDemandes = demandesState.companyDemandes || [];
        return allDemandes
            .filter(d => 
                d.eventId === selectedEvent?.id && 
                d.status === "APPROVED"
            )
            .map(d => d.targetUserId || d.userId || d.user?.id)
            .filter(Boolean);
    }, [demandesState.companyDemandes, selectedEvent?.id]);

    const selectedEventParticipants = assignmentsMap[selectedEvent?.id] || [];
    const selectedEventAvailability = selectedEvent ? getEventRegistrationAvailability(selectedEvent) : null;
    const myEventDemande = useMemo(() => {
        if (!selectedEvent?.id || !currentUser?.id) return null;

        return (demandesState.myDemandes || []).find((demande) =>
            Number(demande?.eventId) === Number(selectedEvent.id)
            && Number(demande?.targetUserId) === Number(currentUser.id)
        ) || null;
    }, [currentUser?.id, demandesState.myDemandes, selectedEvent?.id]);
    const currentUserAlreadyAssigned = selectedEventParticipants.some(
        (participant) => getParticipantUserId(participant) === Number(currentUser?.id)
    );
    const canUseRegistrationFlow =
        Boolean(selectedEvent)
        && (roleConfig.isCollaborator || roleConfig.isManager || roleConfig.isRh)
        && (!roleConfig.isCollaborator || activeRoleView === "PLANNED_EVENTS");

    const registrationLabel = roleConfig.isCollaborator
        ? "M'inscrire"
        : roleConfig.isManager
            ? "Inscrire mes collaborateurs"
            : "Inscrire les collaborateurs";

    const registrationAction = useMemo(() => {
        if (!selectedEvent || !canUseRegistrationFlow) return null;

        if (roleConfig.isCollaborator) {
            const demandeStatus = String(myEventDemande?.status || "").toUpperCase();
            const isPendingDemande = demandeStatus === "PENDING_MANAGER" || demandeStatus === "PENDING_RF";
            const isApprovedDemande = demandeStatus === "APPROVED";

            if (isPendingDemande && Number(myEventDemande?.targetUserId) === Number(currentUser?.id)) {
                return {
                    label: "Annuler la demande",
                    disabled: false,
                    reason: "Votre demande d'inscription est en attente.",
                    onClick: () => {
                        setDetailsOpen(false);
                        setWithdrawalModalOpen(true);
                    },
                };
            }

            if (isApprovedDemande || currentUserAlreadyAssigned) {
                return {
                    label: "Se désister",
                    disabled: false,
                    reason: "Votre inscription à cet événement est déjà validée.",
                    onClick: () => {
                        setDetailsOpen(false);
                        setWithdrawalModalOpen(true);
                    },
                };
            }
        }

        let disabled = !selectedEventAvailability?.allowed;
        let reason = selectedEventAvailability?.reason || "";

        if (!disabled && roleConfig.isCollaborator && currentUserAlreadyAssigned) {
            disabled = true;
            reason = "Vous êtes déjà affecté à cette session.";
        }

        if (!disabled && roleConfig.isManager && !registrationCandidates.length) {
            disabled = true;
            reason = "Aucun collaborateur direct disponible pour l'inscription.";
        }

        if (!disabled && roleConfig.isRh && !registrationCandidates.length) {
            disabled = true;
            reason = "Aucun collaborateur disponible pour l'inscription.";
        }

        return {
            label: registrationLabel,
            disabled,
            reason,
            onClick: () => {
                if (disabled) return;

                if (roleConfig.isCollaborator) {
                    dispatch(
                        createEventDemandeByLearnerRequest({
                            eventId: selectedEvent.id,
                            targetUserId: currentUser.id,
                            action: selectedEvent.titre,
                            description: selectedEvent.description,
                        })
                    );
                    setDetailsOpen(false);
                    return;
                }

                setDetailsOpen(false);
                setRegistrationPickerOpen(true);
            },
        };
    }, [
        canUseRegistrationFlow,
        currentUser?.id,
        currentUserAlreadyAssigned,
        dispatch,
        registrationCandidates.length,
        registrationLabel,
        roleConfig.isCollaborator,
        roleConfig.isManager,
        roleConfig.isRh,
        selectedEvent,
        selectedEventAvailability,
        myEventDemande,
    ]);

    const absenceAction = useMemo(() => {
        if (!selectedEvent || !roleConfig.isCollaborator || activeRoleView !== "MY_EVENTS") {
            return null;
        }

        let disabled = false;
        let reason = "";

        if (!currentUserAlreadyAssigned) {
            disabled = true;
            reason = "Vous devez etre affecte a cet evenement.";
        } else if (selectedEvent.status === "ANNULE") {
            disabled = true;
            reason = "Impossible de déclarer une absence sur un événement annulé.";
        } else if (selectedEvent.status === "TERMINE") {
            disabled = true;
            reason = "Impossible de déclarer une absence sur un événement terminé.";
        }

        return {
            label: "Declarer mon absence",
            disabled,
            reason,
            onClick: () => {
                if (disabled) return;
                setDetailsOpen(false);
                setAbsenceModalOpen(true);
            },
        };
    }, [activeRoleView, currentUserAlreadyAssigned, roleConfig.isCollaborator, selectedEvent]);

    const handleRegistrationUsersConfirm = (userIds) => {
        if (!selectedEvent?.id || !userIds?.length) return;

        const payload = {
            eventId: selectedEvent.id,
            targetUserIds: userIds.map(Number),
            action: selectedEvent.titre,
            description: selectedEvent.description,
        };

        if (roleConfig.isManager) {
            dispatch(createEventDemandeByManagerRequest(payload));
        } else if (roleConfig.isRh) {
            dispatch(createEventDemandeByRfRequest(payload));
        }

        setRegistrationPickerOpen(false);
    };

    const eventContent = (contentArg) => {
        const { event } = contentArg;
        const rawEvent = event.extendedProps?.rawEvent;
        const availability = getEventRegistrationAvailability(rawEvent);

        return (
            <div className="calendar-event-card">
                <div className="calendar-event-card__title">{event.title}</div>
            </div>
        );
    };

    if (!currentRole?.code) {
        return <CalendarLoader message="Chargement du rôle connecté..." />;
    }

    if (!currentUser) {
        return <CalendarLoader message="Chargement de l'utilisateur connecté..." />;
    }

    if (pageLoading || (needsAssignments && assignmentsLoading)) {
        return <CalendarLoader message="Chargement des événements et affectations..." />;
    }

    const isTeamEventsMode = roleConfig.canSeeTeamFilter && activeRoleView === "TEAM_EVENTS";
    const pageLayoutClassName = isTeamEventsMode ? "formation-calendar-layout" : "calendar-single-pane-layout";
    const pageContentClassName = isTeamEventsMode ? "formation-calendar-content" : "calendar-single-pane-content";

    return (
        <div className="calendar-page">
            <CalendarToolbar
                title={calendarTitle}
                currentView={currentView}
                onViewChange={handleViewChange}
                onToday={() => handleCalendarNav("today")}
                onPrev={() => handleCalendarNav("prev")}
                onNext={() => handleCalendarNav("next")}
                onRefresh={handleCalendarRefresh}
                onCreate={() => setFormState({ open: true, mode: "create", event: null })}
                canCreate={roleConfig.canCreate}
            />

            {/*<CalendarRoleBanner roleConfig={roleConfig} />*/}

            <div className={pageLayoutClassName}>
                {isTeamEventsMode && (
                    <CalendarCollaboratorsSidebar
                        collaborators={managerDirectReports}
                        selectedCollaborator={selectedCollaboratorId}
                        onSelectCollaborator={setSelectedCollaboratorId}
                        loading={Boolean(userState.directReportsLoading)}
                    />
                )}

                <div className={pageContentClassName}>
                    <div className="calendar-shell">
                        <div className="calendar-shell__main">
                            <CalendarFilters
                                statuses={availableStatuses}
                                selectedStatuses={selectedStatuses}
                                onToggleStatus={handleToggleStatus}
                                onResetStatuses={handleResetStatuses}
                                searchTerm={searchTerm}
                                onSearchChange={setSearchTerm}
                                roleConfig={roleConfig}
                                directReports={managerDirectReports}
                                selectedCollaboratorId={selectedCollaboratorId}
                                onCollaboratorChange={setSelectedCollaboratorId}
                                activeView={activeRoleView}
                                onViewChange={setActiveRoleView}
                            />

                            <div className="calendar-board">
                                <FullCalendar
                                    ref={calendarRef}
                                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                                    initialView={currentView}
                                    headerToolbar={false}

                                    height="100%"
                                    contentHeight="100%"
                                    expandRows={true}
                                    stickyHeaderDates
                                    locale="fr"
                                    buttonText={{
                                        today: "Aujourd'hui",
                                        month: "Mois",
                                        week: "Semaine",
                                        day: "Jour",
                                        list: "Événements",
                                    }}
                                    editable={roleConfig.canReplanify}
                                    selectable={roleConfig.canCreate}
                                    eventDrop={handleEventDrop}
                                    eventClick={handleEventClick}
                                    datesSet={(dateInfo) => setCalendarTitle(dateInfo.view.title)}
                                    events={calendarEvents}
                                    eventContent={eventContent}
                                    dayMaxEvents={2}
                                    weekends
                                    nowIndicator
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <EventDetailsModal
                open={detailsOpen}
                event={selectedEvent}
                roleConfig={roleConfig}
                registrationAction={registrationAction}
                absenceAction={absenceAction}
                onClose={() => setDetailsOpen(false)}
                onEdit={() => {
                    setDetailsOpen(false);
                    setFormState({ open: true, mode: "edit", event: selectedEvent });
                }}
                onReplanify={() => {
                    setDetailsOpen(false);
                    setRescheduleState({ open: true, event: selectedEvent });
                }}
                onCancel={confirmCancellation}
                onAssign={handleAssign}
                onSendInvitation={handleSendInvitations}
                onDownloadAttendanceSheet={handleDownloadAttendanceSheet}
            />

            <EventAbsenceModal
                open={absenceModalOpen}
                event={selectedEvent}
                loading={Boolean(attendanceState.submitting)}
                onClose={() => setAbsenceModalOpen(false)}
                onSubmit={(payload) => {
                    dispatch(declareMyEventAbsenceRequest(selectedEvent.id, payload));
                    setAbsenceModalOpen(false);
                }}
            />

            <EventWithdrawalRequestModal
                open={withdrawalModalOpen}
                event={selectedEvent}
                loading={Boolean(demandesState.loading)}
                title={String(myEventDemande?.status || "").toUpperCase() === "APPROVED" ? "Se désister" : "Annuler la demande"}
                motifLabel={String(myEventDemande?.status || "").toUpperCase() === "APPROVED" ? "Motif de désistement *" : "Motif d'annulation *"}
                motifPlaceholder={String(myEventDemande?.status || "").toUpperCase() === "APPROVED" ? "Ex: indisponibilité, changement de priorité..." : "Ex: erreur de demande, indisponibilité..."}
                submitLabel={String(myEventDemande?.status || "").toUpperCase() === "APPROVED" ? "Confirmer le désistement" : "Confirmer l'annulation"}
                errorMessage={String(myEventDemande?.status || "").toUpperCase() === "APPROVED" ? "Le motif de désistement est obligatoire." : "Le motif d'annulation est obligatoire."}
                onClose={() => setWithdrawalModalOpen(false)}
                onSubmit={(payload) => {
                    if (!myEventDemande?.id) return;
                    if (String(myEventDemande?.status || "").toUpperCase() === "APPROVED") {
                        dispatch(
                            withdrawEventDemandeRequest({
                                demandeId: myEventDemande.id,
                                ...payload,
                            })
                        );
                    } else {
                        dispatch(
                            cancelEventDemandeRequest({
                                demandeId: myEventDemande.id,
                                ...payload,
                            })
                        );
                    }
                    setWithdrawalModalOpen(false);
                }}
            />

            <EventFormModal
                open={formState.open}
                mode={formState.mode}
                event={formState.event}
                modules={modules}
                salles={salles}
                onClose={() => setFormState({ open: false, mode: "create", event: null })}
                onSubmit={formState.mode === "edit" ? handleUpdateSubmit : handleCreateSubmit}
                loading={Boolean(eventsState.loading)}
                enablePresenceQr
            />

            <EventRescheduleModal
                open={rescheduleState.open}
                event={rescheduleState.event}
                onClose={() => setRescheduleState({ open: false, event: null })}
                onSubmit={handleRescheduleSubmit}
                loading={Boolean(eventsState.loading)}
            />

            <BulkAssignmentModal
                open={assignmentOpen}
                onClose={() => setAssignmentOpen(false)}
                assignmentType="EVENT"
                targetId={selectedEvent?.id}
                targetLabel={selectedEvent?.titre}
            />

            <UsersPickerModal
                open={registrationPickerOpen}
                onClose={() => setRegistrationPickerOpen(false)}
                users={registrationCandidates}
                title={roleConfig.isManager ? "Inscrire mes collaborateurs" : "Inscrire des collaborateurs"}
                onConfirm={handleRegistrationUsersConfirm}
                assignedIds={approvedRegistrationIds}
            />
        </div>
    );
}

export default CalendarPage;
